import React, {useMemo, useState} from "react";
import {
  AvailabilityEventProto,
  ClassEventProto,
  IKensuCalendarEventProto,
  KensuCalendarEventProto,
  space
} from "../../../../provider_api";
import {useProviderProfile} from "../../../../ProviderProfileProvider";
import './Calendar.css';
import {AvailabilityBlockEditor} from "./AvailabilityBlockEditor";
import {ClassEditor} from "./ClassEditor";
import {SaveCancelEditButtons} from "../../../../components/SaveCancelEditButtons";
import {EditableContext} from "../../../../components/Editable/EditableContext";
import {EditableDateField} from "../../../../components/Editable/EditableDateField";
import {
  fromTimeString,
  time15MinuteIntervals,
  timestampToTimeString,
  toTimeString
} from "../../../../util/DateTimeUtils";
import {EditableSingleSelect} from "../../../../components/Editable/EditableSingleSelect";
import {EditableTimeZoneField} from "../../../../components/Editable/EditableTimeZoneField";
import {getEventType} from "./CalendarUtil";
import IErrorProto = space.kenko.proto.IErrorProto;
import {AppointmentEditor} from "./AppointmentEditor";
import {RpcState} from "../../../../util/RpcState";

type Props = {

  // If this is a new event, this instance should be set to whatever default values
  // are desired. For editing, this is the existing event.
  instance: IKensuCalendarEventProto,
  
  // Hack hack. The servers sends down classes and class bookings separately, so if this
  // editor is for a class, we put bookings in here.
  classBookings: IKensuCalendarEventProto[],

  // The class editor can remove people from bookings...
  onClassBookingDeleted: (bookingId: string) => void,
  
  // The state of a pending save / update operation. This lets us show the save
  // button as pending, and also message any rpc errors.
  mutateRpcState: RpcState

  // Any errors that may have come down in the rpc response, the paths are like
  // "startTimestamp", "provider", etc.
  errors: IErrorProto[];

  // The UI shows up a little differently depending whether it's an edit or
  // a new instance.
  mode: 'create' | 'update',

  onSave: (event: IKensuCalendarEventProto) => void,
  onDelete: () => void,
  onCancel: () => void,

  
}

export type CalendarEventType = 'availability' | 'appointment' | 'class' | 'classBooking';

/**
 * This is the overlay for editing and creating events. It itself does not send
 * any RPCs, the caller is responsible for that.
 */
export const EventModal = (props: Props) => {

  const {business, businessProfile} = useProviderProfile();

  // Instead of notifying the parent every time any value changes, keep the changes here
  // until a save. 
  const [workingCopy, setWorkingCopy] = useState<IKensuCalendarEventProto>(props.instance);

  // Whether it's currently being edited. This is always true in 'create'
  // mode and sometimes true in 'update' mode.
  const [editing, setEditing] = useState(props.mode == 'create');

  const availableStartTimes = useMemo(() => {
    return time15MinuteIntervals(0, 0);
  }, [])

  const availableEndTimes = useMemo(() => {
    const start = new Date(workingCopy.startTimestamp as number)
    return time15MinuteIntervals(start.getHours(), start.getMinutes());
  }, [workingCopy, workingCopy.startTimestamp as number])


  /**
   * These are the event types you're allowed to create through the provider calendar.
   * Appointments cannot be created!! Only clients can book them right now. Maybe we
   * can let providers create appointments later, but it would be tricky to link them
   * to a user in order to send reminders.
   */
  const eventTypeOptions = useMemo(() => {
    const types = [];

    if (business?.businessSettings?.offersAppointments) {
      types.push({label: 'Availability', value: 'availability'});
    }

    if (business?.businessSettings?.offersClasses) {
      types.push({label: 'Class', value: 'class'});
    }

    return types;
  }, [business]);


  const officeOptions = useMemo(() => {
    const options: { value: string, label: string }[] = [{
      value: "", label: "None"
    }
    ]
    businessProfile?.locations?.forEach((office) => {
      options.push({
        value: office.locationId as string,
        label: office.locationName || ('Unnamed Location')
      });
    });
    return options;
  }, [businessProfile]);

  return <EditableContext.Provider value={{
    mode: props.mode,
    editing: editing,
    workingCopy: workingCopy,
    setWorkingCopy: setWorkingCopy,
    errors: props.errors,
  }}>

    <div className="CalendarModalOverlay">
      <div className="CalendarModalContent">

        <div className={'CalendarOverlayHeader'}>

          <div style={{flex: 1}}>
            <h2>{props.mode == 'update' ? `Edit ${getEventType(props.instance)}` : 'Create event'}
            </h2>
          </div>

          <SaveCancelEditButtons isEditing={editing}
                                 rpcState={props.mutateRpcState}
                                 onEdit={() => setEditing(true)}
                                 onCancel={() => {
                                   if (props.mode == 'update') {
                                     // Cancel in edit mode resets to the working copy
                                     setWorkingCopy(props.instance);
                                     setEditing(false);

                                   } else {
                                     props.onCancel();
                                   }
                                 }}
                                 onSave={() => {
                                   console.log('saving')
                                   props.onSave(workingCopy);
                                 }}

          />

          {props.mode == 'update' &&
              <img onClick={props.onDelete} className={'CalendarOverlayCloseButton DarkModeInvert'}
                   src={require('../../../../images/icons/delete.png')} alt={'Delete'}/>
          }

          <img onClick={props.onCancel} className={'CalendarOverlayCloseButton DarkModeInvert'}
               src={require('../../../../images/icons/close.png')} alt={'Close'}/>

        </div>


        {props.mutateRpcState.state == 'error' &&
            <div className={'ErrorText'}>{props.mutateRpcState.message}</div>
        }


        <div className={'CalendarModalRow'}>

          <EditableDateField<IKensuCalendarEventProto>
              label={'Date'}
              errorKey={'date'}
              getter={instance => {
                const date = new Date(instance.startTimestamp as number);
                return {
                  year: date.getFullYear(),
                  month: date.getMonth() + 1,
                  date: date.getDate(),
                }
              }}
              setter={(instance, v) => {

                // Use the existing time but replace year, month, and date
                const updatedStartTime = new Date(instance.startTimestamp as number);
                updatedStartTime.setFullYear(v.year, v.month - 1, v.date);

                const updatedEndTime = new Date(instance.endTimestamp as number);
                updatedEndTime.setFullYear(v.year, v.month - 1, v.date);

                return new KensuCalendarEventProto({
                  ...instance,
                  startTimestamp: updatedStartTime.getTime(),
                  endTimestamp: updatedEndTime.getTime(),
                })
              }}/>

          <EditableTimeZoneField<IKensuCalendarEventProto>
              label={'Time zone'}
              immutable
              field={'timeZoneId'}
              getter={instance => instance.timeZoneId}
              setter={(instance, value) => {
                return new KensuCalendarEventProto({
                  ...instance,
                  timeZoneId: value
                })
              }}
          />
        </div>

        <div className={'CalendarModalRow'}>
          <EditableSingleSelect<IKensuCalendarEventProto, string>
              label={'Start time'}
              errorKey={'startTimestamp'}
              getter={instance => timestampToTimeString(instance.startTimestamp as number)}
              setter={(instance, v) => {
                // When editing the start time, the end timestamp also changes to keep the
                // appointment the same length.
                const existingEventLength = (instance.endTimestamp as number) - (instance.startTimestamp as number);
                
                const updated = new Date(instance.startTimestamp as number)
                const updatedTime = fromTimeString(v);
                updated.setHours(updatedTime.hour);
                updated.setMinutes(updatedTime.minute);

                console.log('new start time: ' + updated)
                console.log('new end time: ' + new Date(updated.getTime() + existingEventLength))
                return new KensuCalendarEventProto({
                  ...instance,
                  startTimestamp: updated.getTime(),
                  endTimestamp: updated.getTime() + existingEventLength,
                })
              }}
              options={availableStartTimes}
          />

          <EditableSingleSelect<IKensuCalendarEventProto, string>
              label={'End time'}
              getter={instance => timestampToTimeString(instance.endTimestamp as number)}
              setter={(instance, v) => {
                const updated = new Date(instance.endTimestamp as number)
                const updatedTime = fromTimeString(v);
                updated.setHours(updatedTime.hour);
                updated.setMinutes(updatedTime.minute);
                return new KensuCalendarEventProto({
                  ...instance,
                  endTimestamp: updated.getTime()
                })
              }}
              options={availableEndTimes}
          />

        </div>

        {/* You can't change an event's type after it's been created. 
        It doesn't make much sense to do so; and leaving the field out
        saves precious real estate. */}
        {props.mode == 'create' &&
            <EditableSingleSelect<IKensuCalendarEventProto, string>
                label={'Event Type'}
                getter={instance => getEventType(instance as KensuCalendarEventProto)}
                setter={(instance, v) => {
                  // It's important to create a new KensuCalendarEventProto instead of
                  // a plain json object because the proto includes the oneof field. 
                  return new KensuCalendarEventProto({
                    ...instance,
                    availability: v == 'availability' ? new AvailabilityEventProto() : undefined,
                    class: v == 'class' ? new ClassEventProto() : undefined,
                    appointment: undefined
                  });
                }}
                errorKey={'eventType'}
                options={eventTypeOptions}
            />
        }

        {workingCopy.availability && <AvailabilityBlockEditor officeOptions={officeOptions}/>}
        {workingCopy.class && <ClassEditor officeOptions={officeOptions} 
                                           classBookings={props.classBookings}
                                           onBookingDeleted={props.onClassBookingDeleted}
        />}
        {workingCopy.appointment && <AppointmentEditor/>}

      </div>
    </div>
  </EditableContext.Provider>
};

